import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  LinkActionResult,
  LinkActionResultTypenames,
} from '@varo/graphql-schema-types';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, Pressable, StyleSheet, View } from 'react-native';
import { TDefaultRendererProps, TNode } from 'react-native-render-html';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface HtmlWithActionsProps {
  html?: string;
  linkActions?: LinkActionResult[];
}

interface TextNode {
  type: 'text';
  data: string;
}
const HtmlWithActions = ({ html, linkActions }: HtmlWithActionsProps) => {
  const { width } = Dimensions.get('window');

  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'openSavings',
  });

  const [drawerContent, setDrawerContent] = useState<string | null>(null);

  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const pressAnalytics = usePressableAnalytics({
    analyticsId: 'upcomingTransactionLearnMoreAnalytics',
    customAttributes: { account_type: 'checking' },
  });

  const handleLinkPress = (href: string) => {
    linkActions?.map((action) => {
      if (
        href.includes(action.id) &&
        action?.__typename ===
          LinkActionResultTypenames.PresentHtmlBottomSheetAction
      ) {
        setDrawerContent(action.content);
        pressAnalytics();
      }
    });
    bottomSheetRef?.current?.present();
  };

  const customRenderers = {
    a: (props: TDefaultRendererProps<TNode>) => {
      const { tnode } = props;
      const href = tnode.attributes.href;
      return (
        <Pressable onPress={() => handleLinkPress(href)}>
          {tnode.domNode?.children.map((child) => (
            <BodyBaseBoldText
              key={child.endIndex}
              numberOfLines={0}
              style={styles.link}
            >
              {((child as unknown as TextNode)?.data as string) || ''}
            </BodyBaseBoldText>
          ))}
        </Pressable>
      );
    },
  };

  return (
    <View>
      <HTMLRenderer
        htmlAsString={html ?? ''}
        contentWidth={width}
        renderers={customRenderers}
      />

      <BottomSheet ref={bottomSheetRef}>
        <View style={styles.main}>
          <BodyBaseText numberOfLines={0} style={styles.text}>
            <HTMLRenderer
              htmlAsString={drawerContent ?? ''}
              contentWidth={width}
              tagsStyles={htmlStyles}
            />
          </BodyBaseText>
          <Button
            label={translatedText('done')}
            onPress={() => bottomSheetRef?.current?.dismiss()}
          />
        </View>
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  link: {
    color: theme.color.interactive.primary,
    textDecorationLine: 'underline',
  },
  main: { gap: theme.spacing.m, paddingHorizontal: theme.spacing.m },
  text: {
    color: theme.color.text.secondary,
  },
});

const htmlStyles = StyleSheet.create({
  // Disabled ESLint for unused styles in this file, as they're passed to the HTMLRenderer component.
  // eslint-disable-next-line react-native/no-unused-styles
  p: {
    margin: 0,
  },
});

export default HtmlWithActions;
